/**
 * Finance hub (web). Mirrors the mobile Finance tab
 * (`app/(app)/(tabs)/overview.tsx`) but on the web sidebar.
 *
 * Layout: page header + sub-tab strip + active sub-tab content.
 *
 * Sub-tabs (matches mobile):
 *   - Dashboard (P&L summary)  → owner-tier only; stubbed on web
 *     with a "Open in mobile for charts" notice — full charting
 *     is mobile-only for v1.
 *   - Expenses   — list + add overhead expenses (orgFinances)
 *   - Staff      — roster + salary + archive
 *   - Payroll    — month preview + post payroll batch
 *   - Attendance — single-day P / H / A marking per staff
 *
 * Per the role matrix:
 *   - SA / Admin / Accountant: all 5 sub-tabs
 *   - Manager: 4 sub-tabs (Dashboard hidden — operational view only)
 *   - Everyone else: locked empty state
 *
 * The sub-tabs are KEYED by URL search param `?tab=` so deep links
 * land on the right one and back/forward navigates between them.
 */
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { canFinance, isManager } from '@shared/perms';

import { useTopbarSearch } from '@/components/chrome/TopbarSearchContext';
import { useCurrentOrgRole } from '@/lib/useCurrentOrgRole';
import { useFullOrganization } from '@/lib/useFullOrganization';

import { ExpensesTab } from '@/routes/finance/ExpensesTab';
import { StaffTab } from '@/routes/finance/StaffTab';
import { PayrollTab } from '@/routes/finance/PayrollTab';
import { AttendanceTab } from '@/routes/finance/AttendanceTab';
import { DashboardTabStub } from '@/routes/finance/DashboardTabStub';

type SegmentKey = 'dashboard' | 'expenses' | 'staff' | 'payroll' | 'attendance';

const ALL_SEGMENTS: { key: SegmentKey; label: string }[] = [
  { key: 'dashboard',  label: 'Dashboard' },
  { key: 'expenses',   label: 'Expenses' },
  { key: 'staff',      label: 'Staff' },
  { key: 'payroll',    label: 'Payroll' },
  { key: 'attendance', label: 'Attendance' },
];

export function FinancePage() {
  const { org } = useFullOrganization();
  const orgId = org?.id ?? null;
  const { role: orgRole, loading: roleLoading } = useCurrentOrgRole();
  const { setPlaceholder } = useTopbarSearch();

  useEffect(() => {
    setPlaceholder('Search expenses, staff…');
    return () => setPlaceholder('Search…');
  }, [setPlaceholder]);

  // Per-role gate. Manager / SA / Admin / Accountant get in.
  const canSeeFinance = canFinance(orgRole);

  // Per-role sub-tab filter — Manager loses Dashboard per product
  // spec (operational view only, no studio P&L).
  const visibleSegments = useMemo(() => {
    if (isManager(orgRole)) {
      return ALL_SEGMENTS.filter((s) => s.key !== 'dashboard');
    }
    return ALL_SEGMENTS;
  }, [orgRole]);

  // URL-keyed segment so deep links + back/forward work.
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedKey = (searchParams.get('tab') as SegmentKey | null) ?? null;
  const segment: SegmentKey =
    requestedKey && visibleSegments.some((s) => s.key === requestedKey)
      ? requestedKey
      : (visibleSegments[0]?.key ?? 'expenses');

  const setSegment = (key: SegmentKey) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', key);
      return next;
    });
  };

  // Loading + locked states
  if (roleLoading) {
    return (
      <div className="page active" style={{ padding: 32, color: 'var(--text-secondary)' }}>
        Loading…
      </div>
    );
  }
  if (!canSeeFinance) {
    return (
      <div
        className="page active"
        style={{
          padding: 48,
          textAlign: 'center',
          color: 'var(--text-secondary)',
        }}
      >
        <i className="ph ph-lock" style={{ fontSize: 48, opacity: 0.5 }} />
        <div style={{ marginTop: 12, fontWeight: 600 }}>
          Finance is locked for your role
        </div>
        <div style={{ marginTop: 4, fontSize: 14 }}>
          Ask a Super Admin, Admin, or Manager to grant you access.
        </div>
      </div>
    );
  }

  return (
    <div className="page active" style={{ display: 'block' }}>
      <div className="projects-header">
        <div className="projects-title-row">
          <h1>Finance</h1>
          <div className="projects-stats">
            Studio overhead, payroll, and attendance
          </div>
        </div>
      </div>

      {/* Sub-tab strip — narrows by role. */}
      <div
        className="filter-bar"
        style={{ marginBottom: 16 }}
      >
        {visibleSegments.map((s) => (
          <button
            key={s.key}
            type="button"
            className={`filter-chip${segment === s.key ? ' active' : ''}`}
            onClick={() => setSegment(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Active sub-tab content */}
      {segment === 'dashboard' ? (
        <DashboardTabStub orgId={orgId} />
      ) : segment === 'expenses' ? (
        <ExpensesTab orgId={orgId} />
      ) : segment === 'staff' ? (
        <StaffTab orgId={orgId} />
      ) : segment === 'payroll' ? (
        <PayrollTab orgId={orgId} />
      ) : (
        <AttendanceTab orgId={orgId} />
      )}
    </div>
  );
}
